import { amax, amin } from './safeMath';
// The engine's click-card maths in the browser — a line-by-line port of cetalingua_engine/tfd.py (wigner_ville,
// contour_metrics), tria.fftrans and figures.click_card's windows, so the card the portal draws for ANY click is the
// approved one (Final/Portal/Pulsed/Click card). Input: the click's local segment exactly as the engine cuts it for
// figures.click_card (bundle.card), plus the engine's settings for that file's profile.

const pyRound = (x) => { const r = Math.round(x); return Math.abs(x % 1) === 0.5 && r % 2 !== 0 ? r - 1 : r; }; // banker's rounding
const npHanning = (M) => (M === 1 ? [1] : Array.from({ length: M }, (_, k) => 0.5 - 0.5 * Math.cos((2 * Math.PI * k) / (M - 1))));
const hanning1 = (n) => Array.from({ length: n }, (_, i) => 1 - 0.5 * (1 + Math.cos((2 * Math.PI * i) / n)));
const pow2ceil = (n) => 2 ** Math.ceil(Math.log2(n));

export function decodeSegment(card, i) {
  const bin = atob(card.q[i]);
  const n = bin.length / 2; const out = new Float64Array(n); const s = card.scale[i] / 32767;
  for (let k = 0; k < n; k += 1) { let v = bin.charCodeAt(2 * k) | (bin.charCodeAt(2 * k + 1) << 8); if (v > 32767) v -= 65536; out[k] = v * s; }
  return out;
}

// figures._seg: 2*half+1 samples centred on p, zero-padded
function seg(y, p, half) {
  const out = new Float64Array(2 * half + 1);
  for (let k = -half; k <= half; k += 1) { const j = p + k; if (j >= 0 && j < y.length) out[k + half] = y[j]; }
  return out;
}

// radix-2 FFT (power-of-two lengths)
function fft2(re, im) {
  const n = re.length;
  for (let i = 1, j = 0; i < n; i += 1) { let bit = n >> 1; for (; j & bit; bit >>= 1) j ^= bit; j ^= bit;
    if (i < j) { [re[i], re[j]] = [re[j], re[i]]; [im[i], im[j]] = [im[j], im[i]]; } }
  for (let len = 2; len <= n; len <<= 1) {
    const a = (-2 * Math.PI) / len; const wr = Math.cos(a); const wi = Math.sin(a);
    for (let i = 0; i < n; i += len) { let cr = 1; let ci = 0;
      for (let k = 0; k < len / 2; k += 1) {
        const h = i + k + len / 2; const vr = re[h] * cr - im[h] * ci; const vi = re[h] * ci + im[h] * cr;
        re[h] = re[i + k] - vr; im[h] = im[i + k] - vi; re[i + k] += vr; im[i + k] += vi;
        const t = cr * wr - ci * wi; ci = cr * wi + ci * wr; cr = t; } }
  }
}

// scipy.signal.hilbert (FFT length = len(x), any length: direct DFT)
// forward DFT of any length, in place: radix-2 FFT for powers of two, else Bluestein (chirp-z) through radix-2
const isPow2 = (n) => (n & (n - 1)) === 0;
export function dftAny(re, im) {
  const n = re.length; if (n <= 1) return;
  if (isPow2(n)) { fft2(re, im); return; }
  let M = 1; while (M < 2 * n - 1) M <<= 1;
  const cr = new Float64Array(n); const ci = new Float64Array(n);
  for (let t = 0; t < n; t += 1) { const a = (Math.PI * ((t * t) % (2 * n))) / n; cr[t] = Math.cos(a); ci[t] = Math.sin(a); }   // e^{+i pi t^2 / n}
  const ar = new Float64Array(M); const ai = new Float64Array(M); const br = new Float64Array(M); const bi = new Float64Array(M);
  for (let t = 0; t < n; t += 1) { ar[t] = re[t] * cr[t] + im[t] * ci[t]; ai[t] = im[t] * cr[t] - re[t] * ci[t]; }   // x * e^{-i pi t^2/n}
  br[0] = cr[0]; bi[0] = ci[0];
  for (let t = 1; t < n; t += 1) { br[t] = cr[t]; bi[t] = ci[t]; br[M - t] = cr[t]; bi[M - t] = ci[t]; }
  fft2(ar, ai); fft2(br, bi);
  for (let k = 0; k < M; k += 1) { const r = ar[k] * br[k] - ai[k] * bi[k]; const i2 = ar[k] * bi[k] + ai[k] * br[k]; ar[k] = r; ai[k] = -i2; }  // conj for inverse
  fft2(ar, ai);
  for (let k = 0; k < n; k += 1) { const r = ar[k] / M; const i2 = -ai[k] / M; re[k] = r * cr[k] + i2 * ci[k]; im[k] = i2 * cr[k] - r * ci[k]; }
}
/** inverse DFT of any length, in place (1/n included) */
export function idftAny(re, im) {
  const n = re.length; for (let k = 0; k < n; k += 1) im[k] = -im[k];
  dftAny(re, im);
  for (let k = 0; k < n; k += 1) { re[k] /= n; im[k] = -im[k] / n; }
}
// scipy.signal.hilbert (analytic signal, FFT length = len(x))
export function hilbert(x) {
  const n = x.length; const re = Float64Array.from(x); const im = new Float64Array(n);
  dftAny(re, im);
  const h = new Float64Array(n); h[0] = 1;
  if (n % 2 === 0) { h[n / 2] = 1; for (let k = 1; k < n / 2; k += 1) h[k] = 2; } else for (let k = 1; k < (n + 1) / 2; k += 1) h[k] = 2;
  for (let k = 0; k < n; k += 1) { re[k] *= h[k]; im[k] *= h[k]; }
  idftAny(re, im);
  return { re, im };
}

/** tfd.wigner_ville(x, fs, lag_window_s, time_smooth_s) → {W[f][t] (Array of Float64Array), times, freqs} */
export function wignerVille(x, fs, lagWindowS, timeSmoothS) {
  const z = hilbert(x); const n = x.length;
  const L = Math.trunc(Math.max(4, Math.min(pyRound((lagWindowS * fs) / 2), Math.floor(n / 2) - 1)));
  const win = npHanning(2 * L + 1);
  const nfft = Math.max(256, pow2ceil(2 * L + 1));
  const W = Array.from({ length: nfft }, () => new Float64Array(n));
  const re = new Float64Array(nfft); const im = new Float64Array(nfft);
  for (let t = 0; t < n; t += 1) {
    re.fill(0); im.fill(0);
    for (let m = -L; m <= L; m += 1) {
      const ip = t + m; const iq = t - m;
      if (ip < 0 || ip >= n || iq < 0 || iq >= n) continue;
      const ar = z.re[ip]; const ai = z.im[ip]; const br = z.re[iq]; const bi = -z.im[iq];
      const kr = (ar * br - ai * bi) * win[m + L]; const ki = (ar * bi + ai * br) * win[m + L];
      const col = m >= 0 ? m : nfft + m;
      re[col] = kr; im[col] = ki;
    }
    fft2(re, im);
    for (let k = 0; k < nfft; k += 1) W[k][t] = re[k];
  }
  if (timeSmoothS) {
    const g = pyRound(timeSmoothS * fs) | 1;
    if (g > 1) {
      const gw = npHanning(g + 2).slice(1, -1); const sum = gw.reduce((a, b) => a + b, 0); const w = gw.map((v) => v / sum);
      const h = Math.floor(g / 2);
      for (let k = 0; k < nfft; k += 1) {
        const row = W[k]; const out = new Float64Array(n);
        for (let t = 0; t < n; t += 1) { let acc = 0; for (let j = 0; j < g; j += 1) { const q = Math.min(n - 1, Math.max(0, t - h + j)); acc += w[j] * row[q]; } out[t] = acc; }
        W[k] = out;
      }
    }
  }
  return { W, times: Array.from({ length: n }, (_, t) => t / fs), freqs: Array.from({ length: nfft }, (_, k) => (k * fs) / (2 * nfft)) };
}

/** tfd.contour_metrics: connected region (4-neighbour) around the maximum where W >= level dB (amplitude). */
export function contourMetrics(W, times, freqs, levelDb = -6) {
  const nf = W.length; const nt = W[0].length;
  let fi = 0; let ti = 0; let mx = -Infinity;
  for (let k = 0; k < nf; k += 1) for (let t = 0; t < nt; t += 1) { const v = Math.max(0, W[k][t]); if (v > mx) { mx = v; fi = k; ti = t; } }
  const thr = mx * 10 ** (levelDb / 20);
  const reg = new Uint8Array(nf * nt); const stack = [[fi, ti]]; reg[fi * nt + ti] = 1;
  while (stack.length) {
    const [k, t] = stack.pop();
    [[k + 1, t], [k - 1, t], [k, t + 1], [k, t - 1]].forEach(([a, b]) => {
      if (a < 0 || a >= nf || b < 0 || b >= nt || reg[a * nt + b]) return;
      if (Math.max(0, W[a][b]) >= thr) { reg[a * nt + b] = 1; stack.push([a, b]); }
    });
  }
  const cols = []; const upper = []; const lower = []; const ridge = [];
  for (let t = 0; t < nt; t += 1) {
    let lo = -1; let hi = -1; let best = -1; let bv = -Infinity;
    for (let k = 0; k < nf; k += 1) if (reg[k * nt + t]) { if (lo < 0) lo = k; hi = k; const v = Math.max(0, W[k][t]); if (v > bv) { bv = v; best = k; } }
    if (lo >= 0) { cols.push(t); upper.push(freqs[hi]); lower.push(freqs[lo]); ridge.push(freqs[best]); }
  }
  const rows = []; for (let k = 0; k < nf; k += 1) if (reg[k * nt + ti]) rows.push(k);
  const dt = times.length > 1 ? times[1] - times[0] : 0;
  return { tMax: times[ti], fMax: freqs[fi], length: (cols[cols.length - 1] - cols[0] + 1) * dt,
    sweep: ridge[ridge.length - 1] - ridge[0], width: freqs[rows[rows.length - 1]] - freqs[rows[0]] + (freqs[1] - freqs[0]),
    upperMax: amax(upper), lowerMin: amin(lower), cols, upper, lower, ridge };
}

/** tria.fftrans: magnitude spectrum of a Hann (hanning1) windowed segment, first nfft/2 bins. */
export function fftrans(x, nfft, fs) {
  const w = hanning1(x.length); const re = new Float64Array(nfft); const im = new Float64Array(nfft);
  for (let i = 0; i < x.length && i < nfft; i += 1) re[i] = x[i] * w[i];
  fft2(re, im);
  const m = nfft / 2;
  return { pxx: Array.from({ length: m }, (_, k) => Math.hypot(re[k], im[k])), f: Array.from({ length: m }, (_, k) => (k * fs) / nfft) };
}

/** Everything figures.click_card computes for one click. */
export function cardData(card, i, src = null) {
  // src = { y, p }: a click the reviewer added, cut from the piece's full band-passed signal at sample p
  const s = card.settings; const fs = card.fs;
  const y = src ? src.y : decodeSegment(card, i); const p = src ? src.p : card.centre[i];
  const hw = Math.max(8, Math.trunc(s.figure_half_window_ms * 1e-3 * fs));
  const sg = seg(y, p, hw);
  const { W, times, freqs } = wignerVille(sg, fs, s.wvd_lag_window_ms * 1e-3, s.wvd_time_smooth_ms * 1e-3);
  const cm = contourMetrics(W, times, freqs, s.wvd_contour_level_db);
  const tMs = times.map((t) => (t - hw / fs) * 1e3);
  const half = Math.max(4, Math.trunc(s.click_half_window_ms * 1e-3 * fs));
  const specSeg = seg(y, p, half);
  const nfft = Math.max(s.nfft, pow2ceil(specSeg.length));
  const { pxx, f } = fftrans(specSeg, nfft, fs);
  const z = hilbert(sg); const env = Array.from(z.re, (v, k) => Math.hypot(v, z.im[k]));
  return { W, times, freqs, tMs, cm, pxx, f, seg: sg, env, hw, fs };
}
